import React, { useCallback, useId, useMemo, useState, type RefObject } from 'react'
import { MenuPanel } from './menu-panel'
import type { PopoverAnchor } from './use-popover-placement'

export interface ContextMenuState<P> {
  /** Call from an element's `onContextMenu`. */
  open: (event: React.MouseEvent<HTMLElement>, payload: P) => void
  close: () => void
  current: { payload: P; point: { x: number; y: number }; origin: HTMLElement } | null
}

/**
 * State for a right-click menu: which item was right-clicked and where. A
 * keyboard menu key fires `contextmenu` at 0／0, so the menu then opens below
 * the element itself instead of in the window's corner.
 */
export function useContextMenu<P>(): ContextMenuState<P> {
  const [current, setCurrent] = useState<ContextMenuState<P>['current']>(null)
  const open = useCallback((event: React.MouseEvent<HTMLElement>, payload: P) => {
    // The custom menu replaces the native one; the desktop app's window-wide
    // guard also prevents it in production, without stopping propagation.
    event.preventDefault()
    const origin = event.currentTarget
    let x = event.clientX
    let y = event.clientY
    if (x === 0 && y === 0) {
      const rect = origin.getBoundingClientRect()
      x = rect.left
      y = rect.bottom
    }
    setCurrent({ payload, point: { x, y }, origin })
  }, [])
  const close = useCallback(() => setCurrent(null), [])
  return { open, close, current }
}

export interface ContextMenuProps<P> {
  menu: ContextMenuState<P>
  label: string
  wide?: boolean
  /** The menu's content for the item that was right-clicked. */
  children: (payload: P) => React.ReactNode
}

/**
 * The right-click menu itself. Render it once, BESIDE the list or region whose
 * items open it — not inside a row: keys pressed in the menu would otherwise
 * bubble through the row to the list's own handlers. The rows only call
 * `menu.open(event, payload)` from `onContextMenu`.
 */
export function ContextMenu<P>({ menu, label, wide = false, children }: ContextMenuProps<P>) {
  const panelId = useId()
  const { current, close } = menu
  const x = current?.point.x ?? 0
  const y = current?.point.y ?? 0
  // A zero-size rect at the pointer stands in for a trigger element.
  const anchor = useMemo<RefObject<PopoverAnchor | null>>(
    () => ({
      current: {
        getBoundingClientRect: () =>
          ({
            left: x,
            right: x,
            top: y,
            bottom: y,
            width: 0,
            height: 0,
            x,
            y,
            toJSON: () => ({}),
          }) as DOMRect,
      },
    }),
    [x, y]
  )
  const origin = current?.origin ?? null
  const onClose = useCallback(
    (refocus: boolean) => {
      close()
      if (refocus) origin?.focus()
    },
    [close, origin]
  )
  if (!current) return null
  return (
    <MenuPanel
      // A second right-click elsewhere is a new menu: remount so focus and
      // placement start over.
      key={`${current.point.x},${current.point.y}`}
      id={panelId}
      label={label}
      wide={wide}
      anchor={anchor}
      align="start"
      focusTarget="first"
      insideRefs={[]}
      onClose={onClose}
    >
      {children(current.payload)}
    </MenuPanel>
  )
}
